import { Fragment, memo, SyntheticEvent, useCallback } from 'react';
import { useNavigate } from 'react-router';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import { Card, CardActionArea, CardContent, CardMedia, IconButton, Tooltip } from '@mui/material';

import { formatDate, useToggle } from '@cutwise/utils';

import { URLService } from '@/dmdrive/core/service/URLService';
import EditProjectModal from '@/dmdrive/drive/components/EditProjectModal/EditProjectModal';
import { DriveModel } from '@/dmdrive/drive/entities/DriveModel';
import { useStore } from '@/dmdrive/store';

import { ProjectDate, ProjectHeader, ProjectPreview, ProjectTitle, ProjectTitleContent } from './ProjectBlock.styles';

interface ProjectBlockProps {
  project: DriveModel;
}

function ProjectBlock(props: ProjectBlockProps) {
  const { project } = props;
  const navigate = useNavigate();
  const { removeProject } = useStore('driveStore');

  const [isEditModalOpen, openEditModal, closeEditModal] = useToggle();
  const [isDeleting, startDeleting, stopDeleting] = useToggle();

  const onEditProject = useCallback((e: SyntheticEvent) => {
    e.stopPropagation();

    openEditModal();
  }, []);

  const onDeleteProject = useCallback(
    async (e: SyntheticEvent) => {
      e.stopPropagation();

      try {
        startDeleting();

        await removeProject(project.id);
      } catch (e) {
        stopDeleting();
      }
    },
    [project.id],
  );

  return (
    <Fragment>
      <Card elevation={2}>
        <CardActionArea
          disabled={isDeleting}
          disableRipple
          component="div"
          onClick={() => navigate(URLService.topic(project.conferenceId))}
        >
          <ProjectHeader
            action={
              <Fragment>
                <Tooltip title="Edit project">
                  <IconButton size="small" onClick={onEditProject}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete project">
                  <IconButton size="small" onClick={onDeleteProject}>
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Fragment>
            }
          />

          <ProjectPreview>
            {project.meta.preview && <CardMedia sx={{ height: '100%', width: '100%' }} image={project.meta.preview} />}

            {!project.meta.preview && <ImageNotSupportedIcon fontSize="large" />}
          </ProjectPreview>

          <CardContent>
            <ProjectDate gutterBottom variant="caption">
              {formatDate.toHumanDate(project.createdAt)}
            </ProjectDate>

            <ProjectTitleContent>
              <ProjectTitle variant="h6">{project.title}</ProjectTitle>
            </ProjectTitleContent>
          </CardContent>
        </CardActionArea>
      </Card>

      <EditProjectModal isOpen={isEditModalOpen} onClose={closeEditModal} project={project} />
    </Fragment>
  );
}

export default memo(ProjectBlock);
